import { useEffect } from 'react';

/**
 * The landing page's scroll engine (handoff "Scroll engine"): one rAF-throttled handler reads
 * every pinned section's progress `p` from getBoundingClientRect and writes the frame straight
 * to the DOM, so nothing re-renders while you scroll. A timeout re-runs it if rAF stalls
 * (background tabs). The portal and the pinned sections ease `p` toward the target (0.12 per
 * frame) so they never jump. Under reduced motion every animation sits on its end state.
 *
 * The one thing that does go through React is the composer demo: `onDemo` gets the derived
 * state whenever it changes — into a store only the composer reads (createDemoStore), never
 * page state. Sections that have scrolled away are stamped `data-offstage`, which pauses every
 * CSS animation under them.
 */
const clamp = (v) => Math.max(0, Math.min(1, v));
const ease = (t) => 1 - Math.pow(1 - t, 3);
const back = (t) => { const c = 1.7; return 1 + (c + 1) * Math.pow(t - 1, 3) + c * Math.pow(t - 1, 2); };

export const DEMO_TEXT = 'Patio beers til 8. Who’s close?';
export const DEMO_TEXT_0 = 'Somebody take me to the lake after work.';
export const DEMO_STEPS = 7;

/**
 * Where the composer demo's state lives — outside React state on purpose. During "Type on it."
 * it changes on every scroll frame; as page state (`useState` in LandingPage) each of those
 * frames re-rendered the whole landing page: eight phone screens, the HUMAN. SVG, the composer.
 * Only the composer subscribes (useSyncExternalStore in DemoPortalSection), so a keystroke
 * re-renders one phone — the cost the handoff's iframe had.
 */
export function createDemoStore(initial) {
  let state = initial;
  const subs = new Set();
  return {
    get: () => state,
    set: (next) => { state = next; subs.forEach((fn) => fn()); },
    subscribe: (fn) => { subs.add(fn); return () => subs.delete(fn); },
  };
}

/** The composer's state at demo progress `f` (0 → 7, one unit per step). */
export function demoStateAt(f, photo) {
  const ds = { step: 1, mode: 'text', photo: null, sheet: null, venue: null, winOn: false, aud: ['friends'], sent: false, text: DEMO_TEXT_0 };
  if (f > 0.45) { ds.mode = 'photo'; ds.photo = photo; }
  if (f > 1) { ds.step = 2; ds.text = DEMO_TEXT.slice(0, Math.max(1, Math.round(DEMO_TEXT.length * clamp((f - 1.05) / 0.75)))); }
  if (f > 2.05 && f < 2.6) ds.sheet = 'venue';
  if (f > 2.6) ds.venue = 3;
  if (f > 3.05 && f < 3.85) ds.sheet = 'win';
  if (f > 3.35) ds.winOn = true;
  if (f > 4) { ds.step = 3; ds.sheet = null; }
  if (f > 4.4) ds.aud = ['friends', 'town'];
  if (f > 5.15) ds.sent = true;
  return ds;
}

export default function useLandingScroll(rootRef, { reduced = false, onDemo = null, photo = null } = {}) {
  useEffect(() => {
    const root = rootRef.current;
    if (!root || typeof window === 'undefined') return undefined;
    const still = reduced;
    const $ = (q) => Array.from(root.querySelectorAll(q));
    const q1 = (q) => root.querySelector(q);
    const st = (el, o) => { if (el) Object.assign(el.style, o); };
    // Setting textContent replaces the text node even when the string is the same — a layout
    // for every label on every frame. Write only on change.
    const txt = (el, s) => { if (el && el.textContent !== s) el.textContent = s; };
    let moving = false;
    let ticking = false;
    let lastY = null;
    let navDir = null;
    let fallback = 0;
    let introRaf = 0;
    let introKill = 0;
    let lastDemoKey = '';
    const progressed = new WeakMap();

    const prog = (el) => {
      const r = el.getBoundingClientRect();
      const t = clamp(-r.top / Math.max(1, r.height - window.innerHeight));
      let cur = progressed.get(el);
      if (cur == null || still) cur = t;
      cur += (t - cur) * 0.12;
      if (Math.abs(t - cur) > 0.0008) moving = true; else cur = t;
      progressed.set(el, cur);
      return cur;
    };

    // Intro: the page opens through a portal.
    const intro = q1('[data-intro]');
    if (intro) {
      const ring = intro.querySelector('[data-intro-ring]');
      const vmin = Math.min(window.innerWidth, window.innerHeight) / 100;
      const far = Math.hypot(window.innerWidth, window.innerHeight) * 0.62;
      const r0 = 15 * vmin * 0.86;
      const paint = (r, rot) => {
        intro.style.background = `radial-gradient(circle at 50% 50%, transparent ${r}px, #000 ${r + 1}px)`;
        if (ring) { ring.style.width = `${r / 0.43}px`; ring.style.transform = `translate(-50%,-50%) rotate(${rot}deg)`; }
      };
      const hide = () => { intro.style.display = 'none'; };
      if (still) { hide(); } else {
        const t0 = performance.now();
        const step = (now) => {
          const t = (now - t0) / 1000;
          if (t < 0.7) paint(r0 * ease(t / 0.7), -t * 90);
          else { const k = ease(clamp((t - 0.9) / 1.1)); paint(r0 + (far - r0) * k, -t * 90); intro.style.opacity = String(1 - clamp((t - 1.7) / 0.3)); }
          if (t < 2.05) introRaf = requestAnimationFrame(step); else hide();
        };
        paint(0, 0);
        introRaf = requestAnimationFrame(step);
        introKill = setTimeout(hide, 2600);
      }
    }

    const reveals = $('[data-reveal]');

    // Off the screen a section stops animating. CSS animations run whether or not they can be
    // seen, and the phones carry ~70 of them (the rail rings, the stage flecks, the ring spins),
    // the hero four blurred blobs, the marquee its scroll — all turning while you read the FAQ.
    // `data-offstage` pauses every animation underneath (landing.css); the observer stamps it on
    // the pinned sections, the host cards, the marquee and the CTA, with a 25% margin so nothing
    // is ever caught still as it comes in. The hero is the engine's (below): it has closed into
    // the portal long before it has scrolled away.
    let io = null;
    if (typeof IntersectionObserver !== 'undefined') {
      io = new IntersectionObserver((entries) => entries.forEach((e) => e.target.toggleAttribute('data-offstage', !e.isIntersecting)), { rootMargin: '25% 0px' });
      $('[data-sx]:not([data-sx="hero"]):not([data-sx="hosts"]), [data-stk], [data-stage]').forEach((el) => io.observe(el));
    }

    const frame = () => {
      ticking = false; moving = false;
      const vh = window.innerHeight;
      const vw = window.innerWidth;
      if (!vh) return;

      // The 2px ring progress bar across the very top.
      const bar = q1('[data-progress]');
      if (bar) { const max = Math.max(1, document.documentElement.scrollHeight - vh); bar.style.transform = `scaleX(${clamp(window.scrollY / max)})`; }

      // The phones: zoomed so a phone and its caption both fit inside 100vh (never below 0.26).
      $('[data-pz="g"]').forEach((el) => {
        let z = Math.min(1, (vh * (vw < 900 ? 0.5 : 0.8)) / 880);
        if (vw < 900) {
          const pin = el.closest('[data-sx]') && el.closest('[data-sx]').firstElementChild;
          const col = pin && [...pin.children].find((c) => c !== el.parentElement && !c.contains(el));
          if (pin && col) {
            const cs = getComputedStyle(pin);
            const room = pin.clientHeight - parseFloat(cs.paddingTop) - parseFloat(cs.paddingBottom) - 16 - col.offsetHeight;
            z = Math.max(0.26, Math.min(z, room / 880));
          }
        }
        el.style.zoom = String(z);
      });
      $('[data-pz="s"]').forEach((el) => { el.style.zoom = String(Math.min(0.9, (vh * 0.74) / 880)); });

      // The nav: hidden until the hero has closed; on a phone it hides on the way down.
      const nav = q1('[data-nav]');
      const heroEl = q1('[data-sx="hero"]');
      if (nav && heroEl) {
        const y = window.scrollY;
        const dy = y - (lastY ?? y);
        lastY = y;
        if (Math.abs(dy) > 4) navDir = dy > 0 ? 'down' : 'up';
        const past = heroEl.getBoundingClientRect().bottom < vh * 0.5;
        const show = past && (vw >= 900 || navDir !== 'down');
        nav.style.transform = show ? 'none' : 'translateY(-100%)';
      }

      // Hero falls back into the portal as you leave it.
      if (heroEl && !still) {
        const r = heroEl.getBoundingClientRect();
        const hp = clamp(-r.top / Math.max(1, r.height - vh));
        const pin = heroEl.querySelector('[data-hero-pin]') || heroEl;
        const c = heroEl.querySelector('[data-hero-content]');
        if (c) { c.style.transform = `scale(${1 - hp * 0.35})`; c.style.opacity = String(1 - clamp(hp * 1.3)); }
        const far = Math.hypot(vw, vh) * 0.62;
        const rr = far * (1 - ease(clamp(hp / 0.85)));
        pin.style.clipPath = hp > 0.001 ? `circle(${rr}px at 50% 50%)` : 'none';
        pin.style.opacity = String(1 - clamp((hp - 0.7) / 0.2));
        pin.style.visibility = hp >= 0.99 ? 'hidden' : 'visible';
        heroEl.toggleAttribute('data-offstage', hp >= 0.99);
        const ring = heroEl.querySelector('[data-hero-ring]');
        if (ring) ring.style.opacity = String(0.32 + hp * 0.5);
      }

      // Through the portal.
      const portal = q1('[data-sx="portal"]');
      if (portal) {
        const pr = portal.getBoundingClientRect();
        const pt = clamp((-pr.top / Math.max(1, pr.height - vh) - 0.06) / 0.66);
        let cur = progressed.get(portal);
        if (cur == null || still) cur = pt;
        cur += (pt - cur) * 0.12;
        if (Math.abs(pt - cur) > 0.0008) moving = true; else cur = pt;
        progressed.set(portal, cur);
        const p = still ? 1 : cur;
        const k = ease(clamp(p / 0.92));
        const vmin = Math.min(vw, vh) / 100;
        const far = Math.hypot(vw, vh) * 0.6;
        const rad = 13 * vmin + (far - 13 * vmin) * k;
        const clip = portal.querySelector('[data-sx-clip]');
        const ring = portal.querySelector('[data-sx-ring]');
        const hint = portal.querySelector('[data-sx-hint]');
        if (clip) clip.style.clipPath = `circle(${rad}px at 50% 50%)`;
        if (ring) { ring.style.width = `${rad * 2.3}px`; ring.style.transform = `translate(-50%,-50%) rotate(${-p * 220}deg)`; ring.style.opacity = String(1 - clamp((p - 0.7) / 0.25)); }
        if (hint) hint.style.opacity = String(1 - clamp(p / 0.12));
      }

      // Words light up.
      const words = q1('[data-sx="words"]');
      if (words) {
        const p = still ? 1 : prog(words);
        const all = words.querySelectorAll('[data-w]');
        const n = all.length;
        all.forEach((el) => { const i = +el.dataset.w; const on = p * (n + 0.6) > i + 0.3; el.style.color = on ? (el.dataset.hot ? '#FF00AA' : '#fff') : '#262626'; });
      }

      // Guests: one phone, four screens.
      const guests = q1('[data-sx="guests"]');
      if (guests) {
        const p = prog(guests);
        const n = 4;
        const idx = Math.min(n - 1, Math.floor(p * n * 0.999));
        for (let i = 0; i < n; i += 1) {
          const on = i === idx;
          const t = guests.querySelector(`[data-g-text="${i}"]`);
          const ph = guests.querySelector(`[data-g-phone="${i}"]`);
          const gb = guests.querySelector(`[data-g-bar="${i}"]`);
          const d = p * n - (i + 0.5);
          const op = clamp((0.62 - Math.abs(d)) / 0.22);
          if (t) { t.style.transition = 'none'; t.style.opacity = String(op); t.style.transform = `translateY(${-d * 30}px)`; t.style.pointerEvents = on ? 'auto' : 'none'; }
          // Only the phone that can be seen keeps its screen moving.
          if (ph) { ph.style.transition = 'none'; ph.style.opacity = String(op); ph.style.transform = `translateY(${-d * 36}px) scale(${1 - Math.min(0.05, Math.abs(d) * 0.05)})`; ph.toggleAttribute('data-offstage', op <= 0); }
          if (gb) gb.style.transform = `scaleX(${clamp(p * n - i)})`;
        }
      }

      // Hosts: stacked cards.
      const cards = $('[data-stk]');
      cards.forEach((c, i) => {
        const next = cards[i + 1];
        const veil = c.querySelector('[data-stk-veil]');
        if (!next) { c.style.transform = 'none'; return; }
        const a = c.getBoundingClientRect();
        const b = next.getBoundingClientRect();
        const o = clamp((a.bottom - b.top) / a.height);
        c.style.transform = still ? 'none' : `scale(${1 - 0.06 * o})`;
        if (veil) veil.style.opacity = String(0.55 * o);
      });

      // Demo A: drive the real composer, then land on the rail.
      const dA = q1('[data-sx="demoA"]');
      if (dA) {
        const p = prog(dA);
        const f = p * DEMO_STEPS;
        dA.querySelectorAll('[data-cap]').forEach((el) => { const i = +el.dataset.cap; const d = f - (i + 0.5); el.style.opacity = String(clamp((0.62 - Math.abs(d)) / 0.22)); el.style.transform = `translateY(${-d * 28}px)`; });
        dA.querySelectorAll('[data-dbar]').forEach((el) => { el.style.transform = `scaleX(${clamp(f - +el.dataset.dbar)})`; });
        if (onDemo) {
          const ds = demoStateAt(f, photo);
          const key = JSON.stringify(ds);
          if (key !== lastDemoKey) { lastDemoKey = key; onDemo(ds); }
        }
        const out = clamp((f - 5.75) / 0.3);
        st(dA.querySelector('[data-comp]'), { opacity: String(1 - out), transform: `scale(${1 - out * 0.85})`, borderRadius: `${out * 50}%`, transformOrigin: '29% 9%' });
        const y = clamp((f - 5.95) / 0.3);
        const you = dA.querySelector('[data-you]');
        if (you) { you.style.transform = `scale(${y ? back(y) : 0})`; you.style.width = `${64 * clamp(y * 3)}px`; you.style.marginRight = `${-12 + 12 * clamp(y * 3)}px`; }
        const lc = dA.querySelector('[data-livecount]');
        txt(lc, f > 6 ? '3 LIVE' : '2 LIVE');
        const tk = clamp((f - 6.2) / 0.3);
        st(dA.querySelector('[data-toast]'), { opacity: String(tk), transform: `translate(-50%, ${(1 - tk) * -20}px)` });
      }

      // Demo B: a Party Starter goes green lit.
      const dB = q1('[data-sx="demoB"]');
      if (dB) {
        const p = prog(dB);
        const k = clamp(p / 0.78);
        const got = Math.round(20 * k);
        const fund = dB.querySelector('[data-fund]'); if (fund) fund.style.width = `${k * 100}%`;
        txt(dB.querySelector('[data-pledged]'), `${got}/20 PLEDGED · OPEN`);
        txt(dB.querySelector('[data-cash]'), `$${(got * 60).toLocaleString('en-US')}`);
        const lit = got >= 20;
        const pillEl = dB.querySelector('[data-lit]');
        if (pillEl) { txt(pillEl, lit ? 'GREEN LIT' : 'FUNDING'); pillEl.style.background = lit ? '#16A34A' : '#EAB308'; pillEl.style.color = lit ? '#fff' : '#000'; pillEl.style.transform = lit ? 'scale(1.08)' : 'scale(1)'; }
        const cta = dB.querySelector('[data-bcta]');
        if (cta) { txt(cta, lit ? 'TICKETS →' : 'PLEDGE →'); cta.style.background = lit ? '#000' : '#FF00FF'; }
        dB.querySelectorAll('[data-bface]').forEach((el) => { const on = +el.dataset.bface < Math.ceil(got / 4); el.style.transform = on ? 'scale(1)' : 'scale(0)'; });
        dB.querySelectorAll('[data-bcap]').forEach((el) => { const i = +el.dataset.bcap; const on = i === (lit ? 1 : 0); el.style.opacity = on ? '1' : '0'; el.style.transform = on ? 'none' : `translateY(${i ? 20 : -20}px)`; });
      }

      reveals.forEach((el) => { if (el.getBoundingClientRect().top < vh * 0.88) { el.style.opacity = '1'; el.style.transform = 'none'; } });

      if (moving) {
        ticking = true;
        requestAnimationFrame(frame);
        clearTimeout(fallback);
        fallback = setTimeout(() => { if (ticking) frame(); }, 80);
      }
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(frame);
      clearTimeout(fallback);
      fallback = setTimeout(() => { if (ticking) frame(); }, 80);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    window.addEventListener('load', frame);
    const first = setTimeout(frame, 50);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      window.removeEventListener('load', frame);
      clearTimeout(first);
      clearTimeout(fallback);
      cancelAnimationFrame(introRaf);
      clearTimeout(introKill);
      if (io) io.disconnect();
    };
  }, [rootRef, reduced, onDemo, photo]);
}
